import { MODULES, PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ModuleGuard, PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { usePmsCredential } from '../../pms-credentials/index.js';
import { useSoftDeleteProperty } from '../api/mutations.js';
import { useProperty } from '../api/queries.js';
import { type PropertyDetail } from '../api/schemas.js';

/**
 * One property.
 *
 * Nothing here handles loading, errors, breadcrumbs or the correlation id —
 * `ResourceDetailPage` owns all four, so this file is only the fields this
 * resource has. That is the whole reason the shell is shared: a dozen detail
 * screens with a dozen copies of the state ordering is a dozen chances to
 * render "not found" while a request is still in flight.
 *
 * `DetailField` renders "Not recorded" for an empty value, so nothing here
 * needs a placeholder of its own.
 */

/** Resolves a PMS adapter id to its human-readable name. */
function PmsAdapterName({ id }: { readonly id: string | null }): React.JSX.Element {
  const credential = usePmsCredential(id ?? '');

  if (id === null) return <>—</>;
  if (credential.isPending || credential.data === undefined) return <>{id}</>;
  return <>{credential.data.name}</>;
}

/** Joins the parts of an address that exist, in reading order. */
function addressOf(property: PropertyDetail): string {
  return [
    property.addressLine1,
    property.addressLine2,
    property.addressLine3,
    property.addressLine4,
    property.city,
    property.stateCode,
    property.postCode,
    property.countryCode,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(', ');
}

export function PropertyDetailPage(): React.JSX.Element {
  const propertyId = useParams().propertyId ?? '';
  const navigate = useNavigate();
  const query = useProperty(propertyId);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeleteProperty();

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(propertyId);
    setConfirmingDelete(false);
    void navigate('/properties');
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.PROPERTY_READ}
      parent={{ to: '/properties', label: 'Properties' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PROPERTY_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/properties/${propertyId}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PROPERTY_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete property"
            description={`${data.name} will be removed from the list and can no longer be sold. Its reservations, payments and history are retained, not erased.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Display name">{data.displayName}</DetailField>
            <DetailField label="Type">{data.propertyType}</DetailField>
            <DetailField label="Star rating">
              {data.starRating === null ? null : `${String(data.starRating)} of 5`}
            </DetailField>
            <DetailField label="Time zone">{data.timezone}</DetailField>
            <DetailField label="Currency">{data.currency}</DetailField>
            <DetailField label="Check-in / check-out">
              {[data.defaultCheckInTime, data.defaultCheckOutTime]
                .filter((part): part is string => part !== null)
                .join(' – ')}
            </DetailField>
            <DetailField label="Email">{data.email}</DetailField>
            <DetailField label="Phone">
              {[data.phoneCode, data.phone]
                .filter((part) => part !== null && part !== '')
                .join(' ')}
            </DetailField>
            <DetailField label="Mobile">{data.mobile}</DetailField>
            <DetailField label="Website">{data.website}</DetailField>
            <DetailField label="Business number">{data.abn}</DetailField>
            <DetailField label="Address">{addressOf(data)}</DetailField>
            <DetailField label="Coordinates">{`${data.latitude}, ${data.longitude}`}</DetailField>
            {/* Same rule as the form: a tenant with no PMS module would see
                this field read "Not recorded" forever, which describes a
                property that could be synchronised and is not, rather than one
                that never could be. */}
            <ModuleGuard module={MODULES.PMS}>
              <DetailField label="PMS configured">{data.pmsConfigured ? 'Yes' : 'No'}</DetailField>
              {data.pmsConfigured ? (
                <>
                  <DetailField label="PMS adapter">
                    <PmsAdapterName id={data.pmsAdapterId} />
                  </DetailField>
                  <DetailField label="PMS property id">{data.pmsPropertyId ?? '—'}</DetailField>
                </>
              ) : null}
            </ModuleGuard>
            <DetailField label="Description">{data.description}</DetailField>
            <DetailField label="Version">{String(data.version)}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
